if (!customElements.get('video-carousel')) {
  customElements.define(
    'video-carousel',
    class VideoCarousel extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.chips = [...this.querySelectorAll('[data-chip]')];
        this.track = this.querySelector('.video-carousel__track');
        this.chipGroup = this.querySelector('.video-carousel__chips');
        if (!this.slides.length || !this.track) return;

        const start = Number(this.dataset.start) || 0;
        this.active = Math.min(Math.max(start, 0), this.slides.length - 1);
        this.position = this.active;
        this.pointerStartX = null;
        this.autoplayDelay = (Number(this.dataset.autoplay) || 0) * 1000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.preview = this.hasAttribute('data-preview');
        this.sound = this.hasAttribute('data-sound');
        this.isHovered = false;
        this.hasFocus = false;
        this.isVisible = false;

        this.onChipClick = this.onChipClick.bind(this);
        this.onTrackClick = this.onTrackClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onEnded = this.onEnded.bind(this);
        this.onPlaying = this.onPlaying.bind(this);
        this.onGesture = this.onGesture.bind(this);
        this.onPause = () => {
          if (!document.hidden) this.updatePreview();
        };
        this.onPointerEnter = () => this.setPaused('isHovered', true);
        this.onPointerLeave = () => this.setPaused('isHovered', false);
        this.onFocusIn = () => this.setPaused('hasFocus', true);
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.setPaused('hasFocus', false);
        };

        this.chipGroup?.addEventListener('click', this.onChipClick);
        this.track.addEventListener('click', this.onTrackClick);
        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('ended', this.onEnded, true);
        this.addEventListener('playing', this.onPlaying, true);
        this.addEventListener('pause', this.onPause, true);

        this.render(this.position);
        this.settle();
        requestAnimationFrame(() => {
          this.readyFrame = requestAnimationFrame(() => {
            this.dataset.ready = '';
          });
        });

        if ((this.autoplayDelay && !this.reducedMotion) || this.preview) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              this.isVisible = entry.isIntersecting;
              this.scheduleAutoplay();
              this.updatePreview();
            },
            { threshold: 0.5 }
          );
          this.observer.observe(this.querySelector('.video-carousel__viewport') || this);
        }
      }

      disconnectedCallback() {
        this.chipGroup?.removeEventListener('click', this.onChipClick);
        this.track?.removeEventListener('click', this.onTrackClick);
        this.track?.removeEventListener('pointerdown', this.onPointerDown);
        this.track?.removeEventListener('pointerup', this.onPointerUp);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.removeEventListener('ended', this.onEnded, true);
        this.removeEventListener('playing', this.onPlaying, true);
        this.removeEventListener('pause', this.onPause, true);
        this.observer?.disconnect();
        clearTimeout(this.autoplayTimer);
        cancelAnimationFrame(this.readyFrame);
        cancelAnimationFrame(this.frame);
        this.stopWaitingForGesture();
      }

      waitForGesture() {
        if (this.waitingForGesture) return;
        this.waitingForGesture = true;
        document.addEventListener('touchend', this.onGesture, { passive: true, capture: true });
        document.addEventListener('click', this.onGesture, { capture: true });
      }

      stopWaitingForGesture() {
        if (!this.waitingForGesture) return;
        this.waitingForGesture = false;
        document.removeEventListener('touchend', this.onGesture, { capture: true });
        document.removeEventListener('click', this.onGesture, { capture: true });
      }

      onGesture() {
        this.stopWaitingForGesture();
        this.updatePreview();
      }

      onChipClick(event) {
        const chip = event.target.closest('[data-chip]');
        if (chip) this.goTo(Number(chip.dataset.index));
      }

      onTrackClick(event) {
        if (this.swiped) {
          this.swiped = false;
          return;
        }
        const slide = event.target.closest('[data-slide]');
        if (!slide) return;
        const index = Number(slide.dataset.index);
        if (index !== this.active || this.frame) {
          this.goTo(index);
          return;
        }
        this.togglePlayback(slide);
      }

      onKeyDown(event) {
        if (event.key === 'ArrowRight') this.goTo(this.target + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.target - 1);
      }

      onPointerDown(event) {
        this.pointerStartX = event.clientX;
      }

      onPointerUp(event) {
        if (this.pointerStartX === null) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 50) return;
        this.swiped = true;
        this.goTo(this.target + (distance < 0 ? 1 : -1));
      }

      setPaused(reason, value) {
        this[reason] = value;
        this.scheduleAutoplay();
      }

      scheduleAutoplay() {
        clearTimeout(this.autoplayTimer);
        if (!this.autoplayDelay || this.reducedMotion || this.frame) return;
        if (!this.isVisible || this.isHovered || this.hasFocus) return;
        if (this.slides[this.active].classList.contains('is-playing')) return;
        this.autoplayTimer = setTimeout(() => this.goTo(this.active + 1), this.autoplayDelay);
      }

      wrap(value) {
        const total = this.slides.length;
        let offset = ((value % total) + total) % total;
        if (offset > total / 2) offset -= total;
        return offset;
      }

      goTo(index) {
        const total = this.slides.length;
        const next = ((index % total) + total) % total;
        if (next === this.target && (this.frame || next === this.active)) return;

        clearTimeout(this.autoplayTimer);
        cancelAnimationFrame(this.frame);
        this.stop(this.slides[this.active]);
        this.target = next;

        const from = this.position;
        const distance = this.wrap(next - from);
        const duration = this.reducedMotion ? 0 : Math.min(0.33 + Math.abs(distance) * 0.075, 0.7) * 1000;
        const overshoot = Math.sign(distance) * 0.07;
        const startTime = performance.now();

        this.classList.add('is-moving');
        this.slides.forEach((slide) => slide.classList.remove('is-active'));
        this.updateChips();

        const tick = (now) => {
          const progress = duration ? Math.min((now - startTime) / duration, 1) : 1;
          if (progress < 0.72) {
            const glide = 1 - (1 - progress / 0.72) ** 3;
            this.position = from + (distance + overshoot) * glide;
          } else {
            const settle = (1 - Math.cos(((progress - 0.72) / 0.28) * Math.PI)) / 2;
            this.position = from + distance + overshoot * (1 - settle);
          }
          this.render(this.position);
          if (progress < 1) {
            this.frame = requestAnimationFrame(tick);
            return;
          }
          this.frame = null;
          this.position = next;
          this.active = next;
          this.render(this.position);
          this.settle();
        };
        this.frame = requestAnimationFrame(tick);
      }

      render(position) {
        this.slides.forEach((slide, index) => {
          const offset = this.wrap(index - position);
          const distance = Math.abs(offset);
          const slot = Math.round(offset);
          slide.style.setProperty('--offset', offset.toFixed(4));
          slide.dataset.position =
            slot === 0 ? 'center' : `${Math.abs(slot) > 1 ? 'far-' : ''}${slot < 0 ? 'left' : 'right'}`;
          slide.classList.toggle('is-hidden', distance > 3.5);
        });
      }

      settle() {
        this.target = this.active;
        this.classList.remove('is-moving');
        this.slides.forEach((slide, index) => {
          const isActive = index === this.active;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          slide.querySelector('[data-play]')?.setAttribute('tabindex', isActive ? '0' : '-1');
        });
        this.updateChips();
        this.scheduleAutoplay();
        this.updatePreview();
      }

      updatePreview() {
        this.slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video || slide.classList.contains('is-playing')) return;
          const shouldPreview = this.preview && this.isVisible && index === this.active && !this.frame;
          if (shouldPreview) {
            video.muted = true;
            video.loop = true;
            if (video.paused) {
              video.play().catch((error) => {
                if (error?.name === 'NotAllowedError') this.waitForGesture();
              });
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      onPlaying(event) {
        const video = event.target;
        const slide = video.closest?.('[data-slide]');
        if (!slide || slide.classList.contains('is-video-ready')) return;
        const reveal = () => slide.classList.add('is-video-ready');
        if (typeof video.requestVideoFrameCallback === 'function') {
          video.requestVideoFrameCallback(reveal);
        } else {
          video.addEventListener('timeupdate', reveal, { once: true });
        }
      }

      onEnded(event) {
        const slide = event.target.closest?.('[data-slide]');
        if (!slide || !slide.classList.contains('is-playing')) return;
        this.stop(slide);
        this.updatePreview();
        this.scheduleAutoplay();
      }

      updateChips() {
        this.chips.forEach((chip) => {
          chip.setAttribute('aria-pressed', Number(chip.dataset.index) === this.target ? 'true' : 'false');
        });
      }

      togglePlayback(slide) {
        const video = slide.querySelector('video');
        if (!video) return;
        if (!slide.classList.contains('is-playing')) {
          video.loop = false;
          video.currentTime = 0;
          video.muted = !this.sound;
          video.play().catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
          slide.classList.add('is-playing');
          clearTimeout(this.autoplayTimer);
        } else {
          this.stop(slide);
          this.updatePreview();
          this.scheduleAutoplay();
        }
      }

      stop(slide) {
        if (!slide) return;
        const video = slide.querySelector('video');
        if (video) {
          video.pause();
          video.muted = true;
          video.loop = true;
        }
        slide.classList.remove('is-playing');
      }
    }
  );
}
